import { SpriteIcon } from "@/components/icons/IconSprite";
import { useEffect, useState } from "react";
import { Check, Copy, Facebook, Linkedin, Mail, MessageCircle } from "lucide-react";
import { track } from "@/lib/analytics-client";
import { shareUrl } from "@/lib/site-url";
import { isAppMode, openNativeUrl } from "@/app-bridge/core/wrapper";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocale } from "@/lib/i18n/locale-context";

type Props = {
  /** Annonsens sökväg, t.ex. /a/min-cykel */
  path: string;
  title: string;
  price?: number | null;
  /** Kompakt ikonknapp i stället för knapp med text. */
  iconOnly?: boolean;
  className?: string;
};

/** Liten dela-knapp som kan användas i listor, kort och efter publicering. */
export function ShareMenu({ path, title, price, iconOnly, className }: Props) {
  const { lang, country } = useLocale();
  const t = lang === "es" ? { on: "en Sellavo", share: "Compartir anuncio", copied: "¡Copiado!", email: "Correo electrónico", copy: "Copiar enlace" } : lang === "en" ? { on: "on Sellavo", share: "Share listing", copied: "Copied!", email: "Email", copy: "Copy link" } : { on: "på Sellavo", share: "Dela annonsen", copied: "Kopierad!", email: "E-post", copy: "Kopiera länk" };
  const [copied, setCopied] = useState(false);
  const url = shareUrl(path);
  const text =
    price != null
       ? `${title} – ${price.toLocaleString(lang === "es" ? "es-ES" : lang === "en" ? "en-GB" : "sv-SE")} ${country === "ES" ? "€" : "kr"} ${t.on}`
       : `${title} ${t.on}`;

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    track("share", { meta: { via: "copy" } });
    try {
      const { writeNativeClipboard } = await import("@/app-bridge/core/wrapper");
      if (!(await writeNativeClipboard(url))) await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      /* ignoreras – användaren kan kopiera adressen manuellt */
    }
  };

  const open = (href: string, via: string) => {
    track("share", { meta: { via } });
    // Webben måste öppna direkt i klicket – annars blockerar webbläsaren popupen.
    const fallback = () => {
      if (href.startsWith("mailto:") || href.startsWith("sms:")) {
        window.location.href = href;
        return;
      }
      window.open(href, "_blank", "noopener,noreferrer,width=640,height=640");
    };
    if (!isAppMode()) {
      fallback();
      return;
    }
    void openNativeUrl(href).then((ok) => {
      if (!ok) fallback();
    });
  };

  const native = async () => {
    track("share", { meta: { via: "native" } });
    try {
      const { shareNative } = await import("@/app-bridge/core/wrapper");
      if (await shareNative({ title, text, url })) return true;
    } catch {
      /* faller vidare till webbens delning */
    }
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return true;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return true;
      }
    }
    return false;
  };

  const trigger = iconOnly ? (
    <button
      type="button"
       aria-label={t.share}
       title={t.share}
      className={cn(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent",
        className,
      )}
    >
      {copied ? <Check className="h-4 w-4 text-primary" /> : <SpriteIcon name="share-2" className="h-4 w-4" />}
    </button>
  ) : (
    <button
      type="button"
      className={`inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-accent ${className ?? ""}`}
    >
      {copied ? <Check className="h-4 w-4 text-primary" /> : <SpriteIcon name="share-2" className="h-4 w-4" />}
       {copied ? t.copied : t.share}
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        onClick={async (e) => {
          // Använd systemets delningsruta på mobil om den finns.
          if (await native()) {
            e.preventDefault();
          }
        }}
      >
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-50 w-56">
        <DropdownMenuItem
          onSelect={() =>
            open(
              `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
              "facebook",
            )
          }
        >
          <Facebook className="mr-2 h-4 w-4" /> Facebook
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            open(
              `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
              "linkedin",
            )
          }
        >
          <Linkedin className="mr-2 h-4 w-4" /> LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "whatsapp")
          }
        >
          <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            open(
              `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
              "email",
            )
          }
        >
           <Mail className="mr-2 h-4 w-4" /> {t.email}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void copy()}>
           <Copy className="mr-2 h-4 w-4" /> {t.copy}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
